import React, { Component } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { serializeError } from 'serialize-error';

import typeCheck from 'common/util/typeCheck';
import { USER_ROLE_ADMIN } from 'common/userRoles';

import {
  LISTING_STATUS_ACTIVE,
  LISTING_STATUS_NOT_AVAILABLE,
} from 'common/listingConstants';

import {
  SR_QUEUE_JOB_FRONT_TEXT,
} from 'queue/jobTypes';

import {
  REDIS_KEY_TWILIO_PHONE_ROOMMATE_INVITE,
} from 'common/redisConstants';
import {
  SR_API_ACTION_LOGONAS,
  SR_API_ACTION_ADMIN_SMS_BLASTER,
  SR_API_ACTION_ADMIN_GENERATE_AUTO_LOGIN_SHORT_URLS,
  SR_API_ACTION_ADMIN_UPDATE_SMART_APARTMENT_LISTINGS,
  SR_API_ACTION_ADMIN_UPDATE_LOW_BUDGET_PROPERTY_TYPES,
  SR_API_ACTION_ADMIN_REGENERATE_SUNROOM_PHOTOS,
  SR_API_ACTION_ADMIN_REGENERATE_PARTNER_LISTING_PHOTOS,
  SR_API_ACTION_ADMIN_REGENERATE_PARTNER_WATERMARKS,
  SR_API_ACTION_ADMIN_REGENERATE_LISTING_WATERMARKS,
  SR_API_ACTION_ADMIN_ENSURE_RENTER_HOUSEHOLDS_SCRIPT,
  SR_API_ACTION_ADMIN_ASSIGN_OUT_OF_RANGE_APARTMENT_LOCATOR_LEADS_SCRIPT,
  SR_API_ACTION_ADMIN_AUTO_ASSIGN_HOUSEHOLDS_TO_CANCELLED_TOURS_SCRIPT,
  SR_API_ACTION_ADMIN_MIGRATE_LEASE_DRAFTS,
  SR_API_ACTION_ADMIN_GENERATE_APARTMENTS_BY_PARTNER,
  SR_API_ACTION_SANITIZE_PET_DATA,
  SR_API_ACTION_ADMIN_PIN_SUNROOM_LISTINGS,
  SR_API_ACTION_ADMIN_ADD_LOCATION_POINT_TO_LISTINGS,
  SR_API_ACTION_ADMIN_SCRAPE_RENTLINX_LEADS,
  SR_API_ACTION_RENTLINX_SET_VERIFICATION_COOKIE,
  SR_API_ACTION_ADMIN_RESET_SR_PHOTOS,
  SR_API_ACTION_SUNLOCK_TRIGGER_UPDATE,
  SR_API_ACTION_ADMIN_REFRESH_CUSTOMER_IO_COLLECTION,
  SR_API_ACTION_ADMIN_ENCRYPT_SSNS,
  SR_API_ACTION_ADMIN_MIGRATE_REDIRECTIONS,
  SR_API_ACTION_ADMIN_ARCHIVE_RENTLINX_LEADS,
  SR_API_ACTION_ADMIN_REGENERATE_TENANT_ALERT_REPORTS,
} from 'api/actionTypes';

import Navbar from 'client/components/Navbar';
import SrLink from 'client/components/SrLink/SrLink';
import SrBox from 'client/components/SrBox';
import SrLogonasForm from 'client/components/SrLogonasForm';
import ButtonDeprecated, {
  BUTTON_THEME_BLUE,
} from 'client/components/ButtonDeprecated';
import SrAdminTwilioPhoneForm from 'client/components/SrAdminTwilioPhoneForm';
import SrDataTableAdminPartners from 'client/components/SrDataTableAdminPartners';
import SrAdminGenerateReportModal from 'client/components/SrAdminGenerateReportModal';
import SrUserSettingFilterListDropdown from 'client/components/SrUserSettingFilterListDropdown';

import branch from 'client/core/branch';
import { SCREEN_ID_ADMIN_HOME } from 'client/constants/screenIds';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withApiState from 'client/helpers/withApiState';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles from 'client/helpers/allowedRoles';

import postWithState from 'client/actions/postWithState';
import AdminActions from 'client/actions/Admin';
import AuthActions from 'client/actions/Auth';
import AccountManagerActions from 'client/actions/AccountManager';
import SalesRepActions from 'client/actions/SalesRep';
import LeasingManagerActions from 'client/actions/LeasingManager';
import SrUserSettingPartnerTypeDropdown from 'client/components/SrUserSettingPartnerTypeDropdown';

function extractSmsBlasterMessagesFromCsvString(csvString) {
  typeCheck('csvString::String', csvString);
  const messages = csvString
    .split('\n')
    .filter((line) => line.trim().length)
    .map((line) => ({
      channelId: line.split(',')[0].trim(),
      phone: line.split(',')[1].trim(),
      message: line.includes(',') ? line.split(',').splice(2).join(',').trim() : '',
    }));
  typeCheck('messages::NonEmptyArray', messages);
  if (messages.length === 0) {
    const error = new Error('No valid CSV lines found.');
    error.name = 'InvalidCsvError';
    throw error;
  }
  for (const { channelId, phone, message } of messages) {
    typeCheck('channelId::NonEmptyString', channelId);
    typeCheck('phone::Phone', phone);
    typeCheck('message::NonEmptyString', message);
  }
  return messages;
}

function extractShortUrlsToGenerateFromCsvString(csvString) {
  typeCheck('csvString::String', csvString);
  const messages = csvString
    .split('\n')
    .filter((line) => line.trim().length)
    .map((line) => ({
      phone: line.split(',')[0].trim(),
      redirectTo: line.split(',')[1].trim(),
    }));
  typeCheck('messages::NonEmptyArray', messages);
  if (messages.length === 0) {
    const error = new Error('No valid CSV lines found.');
    error.name = 'InvalidCsvError';
    throw error;
  }
  for (const { phone, redirectTo } of messages) {
    typeCheck('phone::Phone', phone);
    typeCheck('redirectTo::NonEmptyString', redirectTo);
  }
  return messages;
}

@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_ADMIN,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withApiState({
  logonasApiState: {
    action: SR_API_ACTION_LOGONAS,
  },
  smsBlasterApiState: {
    action: SR_API_ACTION_ADMIN_SMS_BLASTER,
  },
  shortUrlGeneratorApiState: {
    action: SR_API_ACTION_ADMIN_GENERATE_AUTO_LOGIN_SHORT_URLS,
  },
  migrateRedirectionApiState: {
    action: SR_API_ACTION_ADMIN_MIGRATE_REDIRECTIONS,
  },
  archiveRentlinxLeadsApiState: {
    action: SR_API_ACTION_ADMIN_ARCHIVE_RENTLINX_LEADS,
  },
  regenerateTenantAlertReportsApiState: {
    action: SR_API_ACTION_ADMIN_REGENERATE_TENANT_ALERT_REPORTS,
  },
})
@withScreenId(SCREEN_ID_ADMIN_HOME)
@withScrollToTop
@branch({
  appUrl: ['env', 'APP_URL'],
  currentUser: ['currentUser'],
  accountManagersList: ['accountManagers', 'listWithPartners'],
  leasingManagersList: ['leasingManagers', 'listWithPartners'],
  salesReps: ['salesReps'],
})
class AdminPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Admin | Sunroom Rentals',
    section: 'Admin',
  })

  static propTypes = {
    logonasApiState: PropTypes.object.isRequired,
    smsBlasterApiState: PropTypes.object.isRequired,
    shortUrlGeneratorApiState: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    appUrl: PropTypes.string.isRequired,
    accountManagersList: PropTypes.array.isRequired,
    salesReps: PropTypes.array.isRequired,
    archiveRentlinxLeadsApiState: PropTypes.object,
  }

  static fetchData(match, inject) {
    return Promise.all([
      AccountManagerActions.getListWithPartners(inject),
      SalesRepActions.getSalesRepList(inject),
      LeasingManagerActions.getListWithPartners(inject),
    ]);
  }

  constructor(props) {
    super(props);
    this._runCronTask = this._runCronTask.bind(this);
    this.state = {
      sanitizeUserDataResult: null,
      smsBlasterCsvString: '',
      smsBlasterResult: null,
      shortUrlGeneratorString: '',
      shortUrlGeneratorResult: null,
      generateReportModalIsOpen: false,
    };
  }

  _runCronTaskOnDemand = () => {
    const cronTaskName = global.prompt('Cron task name:');

    if (
      !cronTaskName
      || !global.confirm(`Are you sure you want to run the cron task "${cronTaskName}"?`)
    ) {
      return;
    }
    this.props.dispatch(AdminActions.runCronTask, { cronTaskName });
  }

  _runCronTask = (cronTaskName) => {
    if (!global.confirm(`Are you sure you want to run the cron task "${cronTaskName}"?`)) {
      return;
    }
    this.props.dispatch(AdminActions.runCronTask, { cronTaskName });
  }

  _updateAllListings = () => {
    this.props.dispatch(AdminActions.updateAllListings);
  }

  _updateSmartApartmentListings = () => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_SMART_APARTMENT_LISTINGS,
      payload: null,
    });
  }

  _updateLowBudgetPropertyTypes = () => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_LOW_BUDGET_PROPERTY_TYPES,
      payload: null,
    });
  }

  _regenerateActiveSunroomPhotos = () => {
    const { dispatch } = this.props;
    if (!global.confirm('Are you sure you want to regenerate all active sunroom photos?')) {
      return;
    }
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_REGENERATE_SUNROOM_PHOTOS,
      payload: {
        filterByStatus: LISTING_STATUS_ACTIVE,
      },
    });
  }

  _regenerateActivePartnerListingPhotos = () => {
    const { dispatch } = this.props;
    if (!global.confirm('Are you sure you want to regenerate all active partner listing photos?')) {
      return;
    }
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_REGENERATE_PARTNER_LISTING_PHOTOS,
    });
  }

  _regenerateInactiveSunroomPhotos = () => {
    const { dispatch } = this.props;
    if (!global.confirm('Are you sure you want to regenerate all inactive sunroom photos?')) {
      return;
    }
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_REGENERATE_SUNROOM_PHOTOS,
      payload: {
        filterByStatus: LISTING_STATUS_NOT_AVAILABLE,
      },
    });
  }

  _sanitizeUserData = () => {
    this.props.dispatch(AdminActions.sanitizeUserData)
      .then((sanitizeUserDataResult) => {
        this.setState({ sanitizeUserDataResult });
      });
  }

  _onLogonasFormSubmit = (formValues) => {
    const { dispatch } = this.props;
    dispatch(AuthActions.logonas, {
      phone: formValues.phone,
      redirectUrl: global.location.origin,
    });
  }

  _onSmsBlasterTextareaChange = (e) => {
    this.setState({
      smsBlasterCsvString: e.target.value,
    });
  }

  _onSmsBlasterSubmit = () => {
    const { dispatch } = this.props;
    const { smsBlasterCsvString } = this.state;
    typeCheck('smsBlasterCsvString::String', smsBlasterCsvString);
    if (!smsBlasterCsvString || !global.confirm('Are you sure you want to blast SMS messages?')) {
      return;
    }
    let messages;
    try {
      messages = extractSmsBlasterMessagesFromCsvString(smsBlasterCsvString);
    }
    catch (error) {
      this.setState({
        smsBlasterResult: {
          error: serializeError(error),
        },
      });
      return;
    }
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_SMS_BLASTER,
      payload: {
        messages: messages,
      },
    }).then((smsBlasterResult) => {
      this.setState({ smsBlasterResult });
    });
  }

  _onShortUrlGeneratorTextareaChange = (e) => {
    this.setState({
      shortUrlGeneratorString: e.target.value,
    });
  }

  _onShortUrlGeneratorSubmit = () => {
    const { dispatch } = this.props;
    const { shortUrlGeneratorString } = this.state;
    typeCheck('shortUrlGeneratorString::String', shortUrlGeneratorString);
    if (!shortUrlGeneratorString || !global.confirm('Are you sure you want to generate short URLs?')) {
      return;
    }
    let shortUrlsToGenerate;
    try {
      shortUrlsToGenerate = extractShortUrlsToGenerateFromCsvString(shortUrlGeneratorString);
    }
    catch (error) {
      this.setState({
        shortUrlGeneratorResult: {
          error: serializeError(error),
        },
      });
      return;
    }
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_GENERATE_AUTO_LOGIN_SHORT_URLS,
      payload: {
        shortUrlsToGenerate: shortUrlsToGenerate,
      },
    }).then((shortUrlGeneratorResult) => {
      this.setState({ shortUrlGeneratorResult });
    });
  }

  _ensureRenterHouseholds = () => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_ENSURE_RENTER_HOUSEHOLDS_SCRIPT,
      payload: null,
    });
  }

  _runAssignOutOfRangeApartmentLocatorLeadsScript = () => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_ASSIGN_OUT_OF_RANGE_APARTMENT_LOCATOR_LEADS_SCRIPT,
      payload: null,
    });
  }

  _runAutoAssingCancelledTours = () => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_AUTO_ASSIGN_HOUSEHOLDS_TO_CANCELLED_TOURS_SCRIPT,
      payload: null,
    });
  }

  _runMigrateLeaseDrafts = () => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_MIGRATE_LEASE_DRAFTS,
    });
  }

  _generatePartnersByPartner = () => {
    if (!global.confirm('Are you sure you want to generate apartments by partner?')) {
      return;
    }

    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_GENERATE_APARTMENTS_BY_PARTNER,
    });
  }

  _sanitizePetData = () => {
    if (!global.confirm('Are you sure you want to sanitize pet data?')) {
      return;
    }

    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_SANITIZE_PET_DATA,
    });
  }

  _pinSunroomListings = () => {
    if (!global.confirm('Are you sure you want to pin the sunroom listings?')) {
      return;
    }

    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_PIN_SUNROOM_LISTINGS,
    });
  }

  _addLocationToListings = () => {
    if (!global.confirm('Are you sure you want to add the location point to listings without it?')) {
      return;
    }

    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_ADD_LOCATION_POINT_TO_LISTINGS,
    });
  }

  _runRentlinxLeadsScrapper = () => {
    if (!global.confirm('Are you sure you want to run the rentlinx leads scraper?')) {
      return;
    }

    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_SCRAPE_RENTLINX_LEADS,
    });
  }

  _setRentlinxVerificationCookie = () => {
    const verificationCookie = global.prompt('Please enter the new Rentlinx verification cookie:');
    if (!verificationCookie) {
      return;
    }

    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_RENTLINX_SET_VERIFICATION_COOKIE,
      payload: {
        verificationCookie,
      },
    });
  }

  _resetSunroomListingPhotos = () => {
    if (!global.confirm('Are you sure you want to reset Sunroom Listing photos?')) {
      return;
    }
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_RESET_SR_PHOTOS,
    });
  }

  _triggerSunlockUpdate = () => {
    if (!global.confirm('Are you sure you want to trigger a full-scale Sunlocks update?')) {
      return;
    }
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_SUNLOCK_TRIGGER_UPDATE,
    });
  }

  _toggleGenerateReportModal = () => {
    this.setState({
      generateReportModalIsOpen: !this.state.generateReportModalIsOpen,
    });
  }

  _updateCustomerIoListingsCollection = () => {
    if (!global.confirm('Are you sure you want to update the Customer.io listings collection?')) {
      return;
    }

    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_REFRESH_CUSTOMER_IO_COLLECTION,
    });
  }

  _encryptSsns = () => {
    if (!global.confirm('Are you sure you want to encrypt SSN fields?')) {
      return;
    }

    if (!global.confirm('You should NOT be doing this. Continue?')) {
      return;
    }

    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_ENCRYPT_SSNS,
    });
  }

  _regeneratePartnerWatermarks = () => {
    const partnerIdInput = global.prompt(
      'This will re-generate the partner listing photo watermarks, only for active listings. ' +
      'Partner ID:'
    );
    if (isNaN(partnerIdInput)) {
      return;
    }
    const { dispatch } = this.props;
    const partnerId = parseInt(partnerIdInput, 10);

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_REGENERATE_PARTNER_WATERMARKS,
      payload: {
        partnerId,
      },
    });
  }

  _regenerateListingWatermarks = () => {
    const listingIdInput = global.prompt(
      'This will re-generate the listing photo watermarks. ' +
      'Listing ID:'
    );
    if (!listingIdInput || isNaN(listingIdInput)) {
      return;
    }
    const { dispatch } = this.props;
    const listingId = parseInt(listingIdInput, 10);

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_REGENERATE_LISTING_WATERMARKS,
      payload: {
        listingId,
      },
    });
  }

  _migrateRedirections = () => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_MIGRATE_REDIRECTIONS,
    });
  }

  _archiveRentlinxLeads = () => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_ARCHIVE_RENTLINX_LEADS,
    });
  }

  _regenerateTenantAlertReports = () => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_REGENERATE_TENANT_ALERT_REPORTS,
    });
  }

  render() {
    const {
      logonasApiState,
      smsBlasterApiState,
      shortUrlGeneratorApiState,
      migrateRedirectionApiState,
      archiveRentlinxLeadsApiState,
      regenerateTenantAlertReportsApiState,
      appUrl,
      dispatch,
      currentUser,
      accountManagersList,
      leasingManagersList,
      salesReps,
    } = this.props;

    const {
      sanitizeUserDataResult,
      smsBlasterCsvString,
      smsBlasterResult,
      shortUrlGeneratorString,
      shortUrlGeneratorResult,
      generateReportModalIsOpen,
    } = this.state;

    return (
      <div className='page-admin'>
        <div className={classnames('adminContainer', 'adminLargeContainer')}>
          <Navbar />
          <div className='section'>
            <span className='headline'>Partners</span>
            <div className='actionsSection'>
              <div className='filtersSection'>
                <div className='filter'>
                  <SrUserSettingFilterListDropdown
                    currentUser={currentUser}
                    settingField='adminPartners.accountManagerUserId'
                    placeholder='Account Manager'
                    optionsList={accountManagersList}
                  />
                </div>
                <div className='filter'>
                  <SrUserSettingFilterListDropdown
                    currentUser={currentUser}
                    settingField='adminPartners.leasingOperationsManagerId'
                    placeholder='Leasing Manager'
                    optionsList={leasingManagersList}
                  />
                </div>
                <div className='filter'>
                  <SrUserSettingFilterListDropdown
                    currentUser={currentUser}
                    settingField='adminPartners.salesRepUserId'
                    placeholder='Sales Rep'
                    optionsList={salesReps}
                  />
                </div>
                <div className='filter'>
                  <SrUserSettingPartnerTypeDropdown
                    currentUser={currentUser}
                    settingField='adminPartners.partnerType'
                    dispatch={dispatch}
                  />
                </div>
              </div>
            </div>
            <SrDataTableAdminPartners
              dispatch={dispatch}
              currentUser={currentUser}
            />
          </div>

          <SrBox maxWidth='60%' margin='0 auto'>
            <div className='section'>
              <span className='headline'>Log in as a User</span>
              <SrLogonasForm
                submitError={logonasApiState.error}
                isSubmitting={logonasApiState.inProgress}
                onSubmit={this._onLogonasFormSubmit}
              />
            </div>

            <div className='section'>
              <span className='headline'>Tools</span>
              <div className='innerSection'>
                <textarea
                  placeholder='cha_6q15, 5125551234, Some message...'
                  disabled={smsBlasterApiState.inProgress}
                  onChange={this._onSmsBlasterTextareaChange}
                >
                  {smsBlasterCsvString}
                </textarea>
                <button
                  className='button blue'
                  type='button'
                  disabled={smsBlasterApiState.inProgress || !smsBlasterCsvString}
                  onClick={this._onSmsBlasterSubmit}
                >
                  {smsBlasterApiState.inProgress ? 'blasting...' : 'blast sms messages'}
                </button>
                {smsBlasterApiState.error && (
                  <pre>
                    {JSON.stringify(smsBlasterApiState.error, null, 2)}
                  </pre>
                )}
                {smsBlasterResult && (
                  <div>
                    <h3>SMS Blaster Results</h3>
                    <p>
                      Please go to
                      <SrLink to={`/admin/queue/jobs/type/${SR_QUEUE_JOB_FRONT_TEXT}?state=active`}>
                        {' the Queue Admin page '}
                      </SrLink>
                      to see the SMS delivery status of this blast.
                    </p>
                    <pre>
                      {JSON.stringify(smsBlasterResult, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              <div className='innerSection'>
                <textarea
                  placeholder='5125551234, /listings'
                  disabled={shortUrlGeneratorApiState.inProgress}
                  onChange={this._onShortUrlGeneratorTextareaChange}
                >
                  {shortUrlGeneratorString}
                </textarea>
                <button
                  className='button blue'
                  type='button'
                  disabled={shortUrlGeneratorApiState.inProgress || !shortUrlGeneratorString}
                  onClick={this._onShortUrlGeneratorSubmit}
                >
                  {shortUrlGeneratorApiState.inProgress ? 'generating short URLs...' : 'generate short urls'}
                </button>
              </div>
              {shortUrlGeneratorApiState.error && (
                <pre>
                  {JSON.stringify(shortUrlGeneratorApiState.error, null, 2)}
                </pre>
              )}
              {shortUrlGeneratorResult && (
                <div>
                  <h3>Short URL generator result</h3>
                  <h5>JSON:</h5>
                  <pre>
                    {JSON.stringify(shortUrlGeneratorResult, null, 2)}
                  </pre>
                  <h5>CSV:</h5>
                  <pre>
                    {shortUrlGeneratorResult.map(({ phone, shortUrl }) => `${phone}, ${shortUrl}`).join('\n')}
                  </pre>
                </div>
              )}

              <SrAdminTwilioPhoneForm
                title='Roommate Invite Phone Number'
                twilioPhoneKey={REDIS_KEY_TWILIO_PHONE_ROOMMATE_INVITE}
                dispatch={dispatch}
              />
            </div>

            <div className='section'>
              <span className='headline'>Admin Pages</span>
              <SrLink to='/admin/tours/requested/all'>
                <span className='buttonLink'>
                  Tours
                </span>
              </SrLink>
              <SrLink to='/admin/partner-sales'>
                <span className='buttonLink'>
                  Partner Sales
                </span>
              </SrLink>
              <SrLink to='/admin/household/wants-to-apply'>
                <span className='buttonLink'>
                  Households
                </span>
              </SrLink>
              <SrLink to='/admin/household-overview'>
                <span className='buttonLink'>
                  Household Overview
                </span>
              </SrLink>
              {sanitizeUserDataResult && (
                <pre>
                  {JSON.stringify(sanitizeUserDataResult, null, 2)}
                </pre>
              )}
              <a className='buttonLink' href='/admin/queue'>Queue</a>
              <SrLink to='/admin/user-tour-creator'>
                <span className='buttonLink'>
                  User & Tour Creator
                </span>
              </SrLink>
              <SrLink to='/admin/edit-user'>
                <span className='buttonLink'>
                  Edit user
                </span>
              </SrLink>
              <SrLink to='/admin/tour-times'>
                <span className='buttonLink'>
                  Tour times
                </span>
              </SrLink>
              <SrLink to='/admin/tour-guide-schedules'>
                <span className='buttonLink'>
                  Tour Guide Schedules
                </span>
              </SrLink>
              <SrLink to='/admin/write-push-notification'>
                <span className='buttonLink'>
                  Push notification
                </span>
              </SrLink>
              <SrLink to='/admin/front-channels'>
                <span className='buttonLink'>
                  Front Channels
                </span>
              </SrLink>
              <SrLink to='/admin/locator-leaderboard'>
                <span className='buttonLink'>
                  Apartment Locator Leaderboard
                  <span
                    role='img'
                    aria-label='al-leaderboard'
                  >
                    🏆
                  </span>
                </span>

              </SrLink>
              <SrLink to='/admin/tour-guide-leaderboard'>
                <span className='buttonLink'>
                  Tour Guide Leaderboard
                  <span
                    role='img'
                    aria-label='tg-leaderboard'
                  >
                    🥇🥈🥉
                  </span>
                </span>
              </SrLink>
              <SrLink to='/admin/sunlocks'>
                <span className='buttonLink'>
                  Sunlocks
                </span>
              </SrLink>
              <SrLink to='/admin/codebox'>
                <span className='buttonLink'>
                  Codebox
                </span>
              </SrLink>
              <SrLink to='/admin/deployments'>
                <span className='buttonLink'>
                  Deployments
                </span>
              </SrLink>
              <SrLink to='/admin/household-admin-assignment-control'>
                <span className='buttonLink'>
                  Household Admin Assignment Control
                </span>
              </SrLink>
              <SrLink to='/admin/partner-listings-scraper'>
                <span className='buttonLink'>
                  Partners Listing Scraper
                </span>
              </SrLink>
              <SrLink to='/admin/partner-tenant-screening-criteria'>
                <span className='buttonLink'>
                  Partners Tenant Screening Criteria
                </span>
              </SrLink>
              <SrLink to='/admin/apartments'>
                <span className='buttonLink'>
                  Apartments
                </span>
              </SrLink>
              <SrLink to='/admin/going-to-market'>
                <span className='buttonLink'>
                  Going to Market
                </span>
              </SrLink>
            </div>

            <div className='section'>
              <span className='headline'>Listing Links</span>
              <SrLink to='/admin/pinned'>
                <span className='buttonLink'>
                  Pinned Listings
                </span>
              </SrLink>
              <SrLink to='/admin/edit-listing'>
                <span className='buttonLink'>
                  Edit External Listing
                </span>
              </SrLink>
              <SrLink to='/admin/listings'>
                <span className='buttonLink'>
                  Sunroom Listings
                </span>
              </SrLink>
              <SrLink to='/admin/listing-vitals'>
                <span className='buttonLink'>
                  Listing Vitals
                </span>
              </SrLink>
            </div>

            <div className='section'>
              <span className='headline'>Finance Links</span>

              <SrLink to='/admin/finances/merchants'>
                <span className='buttonLink'>
                  Merchants
                </span>
              </SrLink>
              <SrLink to='/admin/finances/invoices/all'>
                <span className='buttonLink'>
                  Invoices
                </span>
              </SrLink>
              <SrLink to='/admin/finances/leases/all'>
                <span className='buttonLink'>
                  Leases
                </span>
              </SrLink>
              {/* <SrLink className='buttonLink' to='/admin/finances/deposits'>Deposits</SrLink> */}
              {/* <SrLink className='buttonLink' to='/admin/finances/locator-commissions'>
              Locator Commissions
            </SrLink> */}
              {/* <SrLink
              className='buttonLink'
              to='/admin/finances/locator-commission-structures'
            >
              Apartment Locator Commission Structures
            </SrLink> */}
              <SrLink to='/admin/redirections'>
                <span className='buttonLink'>
                  Redirections
                </span>
              </SrLink>
              <SrLink to='/admin/apartment-complex-unit-groups'>
                <span className='buttonLink'>
                  Apartment Complex Units Groups
                </span>
              </SrLink>
            </div>

            <div className='section'>
              <span className='headline'>Income Verification POCs</span>

              <SrLink to='/closing-docs-test?userId=190620&listingId=396628'>
                <span className='buttonLink'>
                  The Closing Docs POC
                </span>
              </SrLink>

              <SrLink to='/verifast-poc'>
                <span className='buttonLink'>
                  Verifast POC
                </span>
              </SrLink>

              <SrLink to='/finicity-poc?userId=190620&listingId=396628'>
                <span className='buttonLink'>
                  Finicity POC
                </span>
              </SrLink>

              <SrLink to='/pinwheel-poc?userId=190620&listingId=396628'>
                <span className='buttonLink'>
                  Pinwheel POC
                </span>
              </SrLink>
            </div>

            <div className='section'>
              <span className='headline'>SunSetup</span>
              <SrLink to='/sunsetup/orders'>
                <span className='buttonLink'>
                  Dashboard
                </span>
              </SrLink>
              <SrLink to='/sunsetup/driver/create'>
                <span className='buttonLink'>
                  Add Driver
                </span>
              </SrLink>
              <SrLink to='/sunsetup/orders/create'>
                <span className='buttonLink'>
                  Create Job
                </span>
              </SrLink>
            </div>

            {generateReportModalIsOpen && (
              <SrAdminGenerateReportModal
                onClose={this._toggleGenerateReportModal}
              />
            )}

            <div className='section updateData'>
              <span className='headline'>Update Data</span>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('updateListings')}
              >
                Cron: Update listings
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('syncSmartApartments')}
              >
                Sync SmartApartments
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._updateSmartApartmentListings()}
              >
                Update Apartments
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._updateAllListings()}
              >
                RealSavvy full update
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._sanitizeUserData()}
              >
                sanitize user data
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('flushRedisQueue')}
              >
                Flush redis queue
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('dailyApartmentLocatorAssignmentCronTask')}
              >
                Cron: Assign Apt Locators
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('dailyAutoFailOldToursCronTask')}
              >
                Cron: Fail Old Tours
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('dailyMovingSoonReminderCronTask')}
              >
                Cron: Moving Soon Reminder
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._updateLowBudgetPropertyTypes()}
              >
                Update low budget renters
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._regenerateActivePartnerListingPhotos}
              >
                Regen Partner Listing Photos
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._regenerateInactiveSunroomPhotos}
              >
                Regen Inactive Sunroom Photos
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('dailyUpdatedListingsNotificationCronTask')}
              >
                Cron: Updated listings email
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('sendEmailFiveMostSavedHomes')}
              >
                Cron: Top Saved Rentals
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('dailyUserSignUpDrip')}
              >
                cron: user no tour email drip
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('syncHomebaseEmployeeSchedules')}
              >
                cron: sync homebase employee schedules
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('delayedGoingToMarketListingsCheck')}
              >
                cron: delayed going to market listings
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._ensureRenterHouseholds()}
              >
                Ensure Renter Households
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runAssignOutOfRangeApartmentLocatorLeadsScript()}
              >
                Assign Out of range Locator Leads Script
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._runAutoAssingCancelledTours}
              >
                Auto-assign cancelled tours
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._runMigrateLeaseDrafts}
              >
                Migrate lease drafts
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('financeAlerts')}
              >
                Cron: Finance Alerts
              </ButtonDeprecated>
              <a
                href={`${appUrl}/quickbooks/connect`}
                target='_blank'
                rel='noopener noreferrer'
              >
                Regenerate QuickBooks access token.
              </a>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('quickbooksSanityCheck')}
              >
                QuickBooks Integration Test
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('scrapeRentlinx')}
              >
                Cron: Scrape Rentlinx
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('scrapeSupra')}
              >
                Cron: Scrape Supra
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._generatePartnersByPartner()}
              >
                Generate apartments by partner
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._sanitizePetData()}
              >
                Sanitize pet data
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._pinSunroomListings()}
              >
                Pin sunroom listings
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._addLocationToListings()}
              >
                Add location to listings
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runRentlinxLeadsScrapper()}
              >
                cron: scrape rentlinx leads since 90 days ago
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._setRentlinxVerificationCookie()}
              >
                Set Rentlinx verification cookie
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._resetSunroomListingPhotos()}
              >
                Re-set Sunroom Listing photos
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={() => this._runCronTask('householdsMoveInDateCheck')}
              >
                Cron: Households move in date check
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._runCronTaskOnDemand}
              >
                Run cron task
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._testNpsSurveyText}
              >
                Test NPS survey text
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._triggerSunlockUpdate}
              >
                Update Sunlocks
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._toggleGenerateReportModal}
              >
                Generate Traffic Report
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._updateCustomerIoListingsCollection}
              >
                Update Customer.io Listings Collection
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._encryptSsns}
              >
                Encrypt SSN fields
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._regeneratePartnerWatermarks}
              >
                Generate Partner Watermarks
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._regenerateListingWatermarks}
              >
                Generate Listing Watermarks
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._migrateRedirections}
                disabled={migrateRedirectionApiState.inProgress}
              >
                Migrate redirections
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._archiveRentlinxLeads}
                disabled={archiveRentlinxLeadsApiState.inProgress}
              >
                Archive Rentlinx leads
              </ButtonDeprecated>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._regenerateTenantAlertReports}
                disabled={regenerateTenantAlertReportsApiState.inProgress}
              >
                Regenerate Tenant Alert Reports
              </ButtonDeprecated>
            </div>
          </SrBox>
        </div>
      </div>
    );
  }
}

AdminPage.displayName = 'AdminPage';

export default AdminPage;
